import { AddIntegrationChannelDrawerFooter } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerFooter';
import { AddIntegrationChannelDrawerHeader } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { SelectIntegrationWorkspace } from '@modules/integrationsV2/components/SelectIntegrationWorkspace';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { AddIntegrationChannelDrawerContentProps } from '@modules/integrationsV2/types';
import { getDescriptionMapInputForIntegrationChannel } from '@modules/integrationsV2/utils';
import { Autocomplete, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDescriptionInput,
  QualtricsSurveyInput,
} from 'generated/graphql';
import { useState } from 'react';
import useGetIntegrationEditFormData from 'v2/core/repository/edge-server/integrations/editIntegrations/useGetIntegrationEditFormData';
import { IsLoading } from 'v2/core/repository/edge-server/types';

export const AddQualtricsIntegrationChannelDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationType,
  integrationData,
  channelDescriptions,
}: AddIntegrationChannelDrawerContentProps) => {
  const [selectedSurvey, setSelectedSurvey] =
    useState<QualtricsSurveyInput | null>(null);
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState(integrationID);
  const [descriptionInput, setDescriptionInput] = useState('');

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Qualtrics,
      integrationType,
    });

  const { data: integrationFormData, status: integrationFormStatus } =
    useGetIntegrationEditFormData({ integrationID });

  const surveys = integrationFormData?.qualtrics?.surveys ?? [];

  const existingSurveys = integrationData?.qualtrics?.surveys ?? [];

  const [
    validateForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Qualtrics,
    integrationType,
    hasValidFormFields: !!selectedSurvey,
    descriptionInput,
    currentDescription: '',
  });

  const integrateQualtricsSurvey = async (
    selectedSurvey: QualtricsSurveyInput | null,
    addAnother = false,
  ) => {
    const isValid = await validateForm();

    if (!isValid || !selectedSurvey) {
      return;
    }

    let updateDesciptionInput: IntegrationDescriptionInput | undefined;

    if (descriptionInput) {
      updateDesciptionInput = {
        descriptionMap: getDescriptionMapInputForIntegrationChannel(
          channelDescriptions,
          {
            description: descriptionInput.trim(),
            sourceID: selectedSurvey.id,
          },
        ),
      };
    }

    // Remove duplicates
    const selectedSurveys = [
      ...new Map(
        [...existingSurveys, selectedSurvey].map((survey) => [
          survey.id,
          survey,
        ]),
      ).values(),
    ];

    const { errors } = await updateIntegration({
      integrationID: selectedWorkspaceID,
      isActive: true,
      updateData: {
        qualtrics: {
          surveys: selectedSurveys,
        },
      },
      updateDescription: updateDesciptionInput,
    });

    if (!!errors?.length) {
      return;
    }

    if (!addAnother) {
      onDrawerClose();
      return;
    }

    setSelectedSurvey(null);
    setDescriptionInput('');
  };

  const addedSurveyIDsSet = new Set(existingSurveys.map((s) => s.id));

  const options = surveys.filter((survey) => !addedSurveyIDsSet.has(survey.id));

  return (
    <Stack height="100%" justifyContent="space-between">
      <Stack>
        <AddIntegrationChannelDrawerHeader title="Integrate a Qualtrics Survey" />
        <Stack spacing={4} p={3}>
          <SelectIntegrationWorkspace
            selectedWorkspaceID={selectedWorkspaceID}
            onChange={(workspaceID) => setSelectedWorkspaceID(workspaceID)}
            channelType={IntegrationChannelType.Qualtrics}
            integrationType={integrationType}
          />
          <Autocomplete
            fullWidth
            multiple={false}
            value={selectedSurvey}
            options={options}
            getOptionLabel={(option) => option?.name}
            onChange={(_, selected) => selected && setSelectedSurvey(selected)}
            renderInput={(params) => (
              <TextField {...params} label="Select Qualtrics Survey" required />
            )}
            componentsProps={{ paper: { elevation: 8 } }}
            loading={IsLoading(integrationFormStatus)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            clearIcon={null}
          />
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isValidationInProgress={isValidatingDescription}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Stack>
      <AddIntegrationChannelDrawerFooter
        onDrawerClose={onDrawerClose}
        onIntegrateAndAddAnother={() =>
          integrateQualtricsSurvey(selectedSurvey, true)
        }
        onIntegrate={() => integrateQualtricsSurvey(selectedSurvey)}
        isIntegrating={IsLoading(updateIntegrationStatus)}
        isDisabled={isDisabled}
        shouldOpenCancelConfirmationDialog={
          !!selectedSurvey || !!descriptionInput
        }
      />
    </Stack>
  );
};
